import { useState } from 'react';
import PageContainer from 'components/PageContainer';
import downloadFile from 'utils/downloadFile';
import base64ToBlob from 'utils/base64ToBlob';
import Table, { FooterCell, FooterRow, TableActions } from 'components/Table';
import {
  numberToCurrencyString,
  dateToLabel,
  numberToDisplayString,
  getExitedCompanyLabelWithShareClass,
} from 'further-ui/utils';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import { makeStyles } from 'tss-react/mui';
import { CloudDownload, Delete, Edit, Email } from '@mui/icons-material';
import { CircularProgress, MenuItem, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useGetRole } from 'hooks/ui/useGetRole';
import AlertDialog from 'components/AlertDialog';
import {
  EXITS_ADD,
  createExitEditRoute,
  createExitStatementAddRoute,
  createExitStatementPdfDownloadRoute,
  createUpdateExitStatementRoute,
} from 'adminConstants/routes';
import { MuiDateRangePicker } from 'components/FormElements/DateRangePicker';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import AppTextInput from 'components/FormElements/AppTextInput';
import { useDebounce } from 'usehooks-ts';
import { FirmSelector } from 'components/FirmSelector';
import { BackgroundExportTypes } from 'further-types/background-exports';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import { sumBy } from 'lodash';
import api, { ApiResponses } from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type ExitLogItem = {
  _id?: string;
  exitId?: string;
  exitAmendmentId?: string | null;
  exitValue?: number;
  valueBeforeDeductions?: number;
  valueAfterDeductions?: number;
  taxableValue?: number | null;
  accruedFeesCharged?: number;
  performanceFeesCharged?: number;
  sharePrice?: number | null;
  taxableValueSharePrice?: number | null;
  date?: string | null;
  isInitial?: boolean | null;
  isShareConsiderationExit?: boolean;
  receivedValueSharePrice?: number;
  considerationShareValue?: number;
  totalSharesSold?: number;
  considerationShares?: number;
  considerationCompany?: string;
  deleteForbidden?: boolean;
  exitStatementId?: string;
  exitStatementSubmitDate?: string | null;
  exitStatementStatus?: string;
  isDraftExitStatement?: boolean;
};

const useStyles = makeStyles()(() => ({
  exitLogContainer: {
    marginBottom: '1rem',
    boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    borderRadius: '4px',
  },
  cmTableDarkHeading: {
    '& th': {
      backgroundColor: '#f0f0f0',
    },
  },
}));

const StyledActionBox = styled(Box)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '14px',
  fontWeight: 500,
  display: 'flex',
  gap: theme.spacing(4),
  alignItems: 'center',
  padding: theme.spacing(0.5, 0),
}));

const StyledProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const StyledIcon = styled(CloudDownload)(({ theme }) => ({
  fontSize: '22px',
  color: theme.palette.primary.main,
}));

const ListExits = () => {
  const { classes } = useStyles();
  const deleteDisclosure = useDisclosure();
  const { isSuperAdmin } = useGetRole();
  const [downloadingExitId, setDownloadingExitId] = useState<string | null>(
    null,
  );
  const [canCreateExit, canDeleteExit, canCreateReport] = useHasPermissions([
    RolePermission.CreateExit,
    RolePermission.DeleteExit,
    RolePermission.CreateReport,
  ]);
  const apiUtils = api.useUtils();

  const downloadExit = api.exit.download.useMutation(
    useHandleTrpcMutation<ApiResponses['exit']['download']>()({
      onSuccess: (data) => {
        const blob = base64ToBlob(data.file, data.mimeType);
        downloadFile(blob, data.filename || `Exit-${Date.now()}.xlsx`);
      },
    }),
  );

  const pagination = usePagination({
    id: 'exits-table',
    orderBy: 'date',
    order: 'desc',
  });

  const { filters, handleChangeFilters } = useFilters<{
    firmId?: string;
    companyName: string;
    dateRange: { startDate?: Date; endDate?: Date };
  }>('exits-table', {
    onFiltersChange: pagination.toFirstPage,
    defaultFilters: {
      firmId: undefined,
      companyName: '',
      dateRange: {
        startDate: undefined,
        endDate: undefined,
      },
    },
  });

  const debouncedSearch = useDebounce(filters.companyName, 300);

  const exits = api.exit.list.useQuery({
    ...(filters.firmId ? { firmId: filters.firmId } : {}),
    companyName: debouncedSearch,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    startDate: filters.dateRange?.startDate
      ? dateToLabel(filters.dateRange.startDate)
      : undefined,
    endDate: filters.dateRange?.endDate
      ? dateToLabel(filters.dateRange.endDate)
      : undefined,
    order: pagination.order,
    orderBy: pagination.orderBy,
  });

  const handleMutation = useHandleTrpcMutation();
  const deleteExit = api.exit.delete.useMutation(
    handleMutation({
      successMessage: 'Exit deleted successfully.',
      refetchType: 'none',
    }),
  );
  const deleteAmendment = api.exit.deleteAmendment.useMutation(
    handleMutation({
      successMessage: 'Exit amendment deleted successfully.',
      refetchType: 'none',
    }),
  );

  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const handleExport = () => {
    createBackgroundExport.mutate({
      exportType: BackgroundExportTypes.ExitsExport,
      ...(filters.firmId ? { firmId: filters.firmId } : {}),
      params: {
        companyName: debouncedSearch,
        startDate: filters.dateRange?.startDate
          ? dateToLabel(filters.dateRange.startDate)
          : undefined,
        endDate: filters.dateRange?.endDate
          ? dateToLabel(filters.dateRange.endDate)
          : undefined,
        order: pagination.order,
        orderBy: pagination.orderBy,
      },
    });
  };

  const handleDownload = async (id: string) => {
    setDownloadingExitId(id);
    downloadExit.mutate(
      { id },
      {
        onSettled: () => {
          setDownloadingExitId(null);
        },
      },
    );
  };

  const onDeleteExit = async () => {
    const exitId = deleteDisclosure.actionProps.id;
    const exitAmendmentId = deleteDisclosure.actionProps.exitAmendmentId;

    if (exitAmendmentId) {
      deleteAmendment.mutate(
        {
          id: exitId,
          exitAmendmentId,
        },
        {
          onSuccess: () => {
            deleteDisclosure.onClose();
            apiUtils.exit.list.invalidate();
          },
        },
      );
    } else {
      deleteExit.mutate(
        { id: exitId },
        {
          onSuccess: () => {
            deleteDisclosure.onClose();
            apiUtils.exit.list.invalidate();
          },
        },
      );
    }
  };

  const columns = [
    ...(isSuperAdmin
      ? [
          {
            label: 'Firm',
            key: 'firmId',
            sort: false,
            render: (elm) => elm?.firm?.firmName,
          },
        ]
      : []),
    {
      label: 'Company',
      key: 'companyLegalName',
      sort: false,
      render: (elm) => {
        return getExitedCompanyLabelWithShareClass(
          elm?.companies?.[0]?.tradingName,
          elm.singleShareClassLabel,
        );
      },
    },
    {
      label: 'First exit date',
      key: 'date',
      render: (elm) => dateToLabel(elm.date),
    },
    {
      label: 'Received value',
      key: 'receivedValue',
      sort: false,
      render: (elm) => numberToCurrencyString(elm.valueAfterDeductions),
    },
    {
      label: 'Taxable value',
      key: 'taxableValue',
      sort: false,
      render: (elm) => numberToCurrencyString(elm.taxableValue),
    },
    {
      label: 'Accrued fees',
      key: 'accruedFeesCharged',
      sort: false,
      render: (elm) => numberToCurrencyString(elm.accruedFeesCharged),
    },
    {
      label: 'Performance fees',
      key: 'performanceFeesCharged',
      sort: false,
      render: (elm) => numberToCurrencyString(elm.performanceFeesCharged),
    },
    {
      label: 'Shares realised',
      key: 'noSharesExited',
      sort: false,
      render: (elm) => numberToDisplayString(elm.noSharesExited),
    },
    {
      label: 'Last edit date',
      key: 'lastEditDate',
      sort: false,
      render: (elm) =>
        elm.lastAmendmentDate ? dateToLabel(elm.lastAmendmentDate) : '-',
    },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (elm) => {
        const isDownloading = downloadingExitId === elm._id;

        return (
          <TableActions
            showAsDropdown
            stopPropagation
            actions={[
              {
                link: createExitEditRoute(elm._id),
                icon: Edit,
                label: 'Update',
                color: 'primary',
                visible: canCreateExit,
              },
              {
                label: 'Download',
                color: 'primary',
                disabled: isDownloading,
                actionComponent: ({ onClick, disabled }) => (
                  <MenuItem onClick={onClick} disabled={disabled}>
                    <StyledActionBox>
                      {isDownloading ? (
                        <StyledProgress size={22} />
                      ) : (
                        <StyledIcon />
                      )}
                      <span>Download</span>
                    </StyledActionBox>
                  </MenuItem>
                ),
                onClick: (e) => {
                  handleDownload(elm._id);
                  e.stopPropagation();
                },
              },
            ]}
          />
        );
      },
    },
  ];

  // Keep the row expanded when deleting to show the exit log context
  const rowIdsToForceExpanded =
    deleteDisclosure.actionProps?.id && exits.data?.result
      ? exits.data.result.map((row, index) => {
          if (row._id === deleteDisclosure.actionProps.id) {
            return `${row._id}-${index}`;
          }
          return '';
        })
      : [];

  const getCollapsedTableColumns = (exitLog: Array<ExitLogItem>) => {
    // Check if there are multiple iterations and if past iterations don't have exit statements
    const hasMultipleIterations = exitLog.length > 1;
    const hasPastIterationsWithoutStatements = exitLog.some(
      (item, index) => index > 0 && !item.exitStatementId,
    );
    const shouldDisablePastIterations =
      hasMultipleIterations && hasPastIterationsWithoutStatements;

    return [
      {
        label: 'Update date',
        key: 'submitDate',
        sort: false,
        render: (elm: ExitLogItem) =>
          elm.date && !elm.isInitial ? dateToLabel(elm.date) : 'Original',
      },

      {
        label: 'Received value',
        key: 'receivedValue',
        sort: false,
        render: (elm: ExitLogItem) =>
          numberToCurrencyString(elm.valueAfterDeductions) || '-',
      },
      {
        label: 'Taxable value',
        key: 'taxableValue',
        sort: false,
        render: (elm: ExitLogItem) => {
          return elm.isShareConsiderationExit
            ? '-'
            : numberToCurrencyString(elm.taxableValue) || '-';
        },
      },
      {
        label: 'Accrued fees',
        key: 'accruedFeesCharged',
        sort: false,
        render: (elm: ExitLogItem) => {
          return elm.isShareConsiderationExit
            ? 'N/A (shares)'
            : numberToCurrencyString(elm.accruedFeesCharged) || '-';
        },
      },
      {
        label: 'Performance fees',
        key: 'performanceFeesCharged',
        sort: false,
        render: (elm: ExitLogItem) => {
          return elm.isShareConsiderationExit
            ? 'N/A (shares)'
            : numberToCurrencyString(elm.performanceFeesCharged) || '-';
        },
      },
      {
        label: 'Received value share price',
        key: 'receivedValueSharePrice',
        sort: false,
        render: (elm: ExitLogItem) => {
          if (elm.isShareConsiderationExit) {
            // Use the pre-calculated share price from the API (rounded to 10 decimal places)
            // to match the calculation in format-financial-metric.ts
            if (elm.receivedValueSharePrice !== undefined) {
              return numberToCurrencyString(elm.receivedValueSharePrice, {
                unlimitedDp: true,
              });
            }
            return '-';
          }
          return numberToCurrencyString(elm.sharePrice, {
            unlimitedDp: true,
          });
        },
      },
      {
        label: 'Taxable value share price',
        key: 'taxableValueSharePrice',
        sort: false,
        render: (elm: ExitLogItem) => {
          return elm.isShareConsiderationExit
            ? '-'
            : numberToCurrencyString(elm.taxableValueSharePrice, {
                unlimitedDp: true,
              });
        },
      },
      {
        label: 'Actions',
        key: 'actions',
        sort: false,
        render: (elm: ExitLogItem, index: number) => {
          // Disable "Send exit statement" for past iterations if there are multiple iterations
          // and past iterations don't have exit statements
          const isPastIteration = index > 0;
          const shouldDisableSendStatement =
            shouldDisablePastIterations &&
            isPastIteration &&
            !elm.exitStatementId;

          const isDraft = elm?.isDraftExitStatement === true;

          return (
            <TableActions
              showAsDropdown
              actions={[
                {
                  label: 'Delete',
                  disabled: elm?.deleteForbidden,
                  icon: Delete,
                  color: 'error',
                  visible: canDeleteExit,
                  tooltip: elm?.deleteForbidden
                    ? 'To delete this exit, subsequent edits must be deleted first.'
                    : undefined,
                  onClick: () =>
                    deleteDisclosure.stageAction({
                      id: elm._id || elm.exitId,
                      exitAmendmentId: elm.exitAmendmentId,
                    }),
                },
                ...(elm?.exitStatementId
                  ? [
                      {
                        link: createUpdateExitStatementRoute(
                          elm.exitStatementId,
                        ),
                        icon: Edit,
                        label: isDraft
                          ? 'Edit draft exit statement'
                          : 'Edit exit statement',
                        color: 'primary' as const,
                      },
                    ]
                  : []),
                {
                  link: !elm?.exitStatementId
                    ? createExitStatementAddRoute(
                        elm.exitId || '',
                        elm.exitAmendmentId || undefined,
                      )
                    : createExitStatementPdfDownloadRoute(
                        elm.exitStatementId || '',
                      ),
                  label: !elm?.exitStatementId
                    ? 'Send exit statement'
                    : 'View exit statement PDFs',
                  icon: Email,
                  visible:
                    (canCreateReport || !!elm.exitStatementId) && !isDraft,
                  color: !elm?.exitStatementId ? 'primary' : 'default',
                  disabled: shouldDisableSendStatement,
                  tooltip: shouldDisableSendStatement
                    ? 'To create an exit statement for this transaction, process the statement for the latest exit update above. The latest exit statement consolidates the original exit and any prior exit updates.'
                    : undefined,
                },
              ]}
            />
          );
        },
      },
    ];
  };

  return (
    <PageContainer heading="Exits">
      <PageContent>
        <PageFilters
          pageId="exits-list"
          buttons={[
            {
              link: EXITS_ADD,
              label: 'Process new exit',
              visible: canCreateExit,
            },
            {
              label: 'Export',
              onClick: handleExport,
              loading: exits.isLoading || isExporting,
              disabled: exits.isLoading || isExporting,
            },
          ]}
        >
          {isSuperAdmin && (
            <Filter>
              <FirmSelector
                placeholder="Select firm"
                firmId={filters.firmId}
                onChange={(firmId) => {
                  handleChangeFilters({ firmId });
                }}
              />
            </Filter>
          )}
          <Filter>
            <AppTextInput
              type="search"
              name="companyName"
              placeholder="Search by company name"
              fullWidth
              value={filters.companyName}
              onChange={(event) => {
                handleChangeFilters({
                  companyName: event.target.value,
                });
              }}
            />
          </Filter>
          <Filter>
            <MuiDateRangePicker
              dateRange={filters.dateRange}
              onDateRangeChange={(dateRange) => {
                handleChangeFilters({
                  dateRange,
                });
              }}
              placeholder="Select date"
            />
          </Filter>
        </PageFilters>
        <Table
          columns={columns}
          tablebody={exits.data?.result || []}
          emptyMessage="No exits have been created yet"
          variant="nohover"
          loading={exits.isLoading}
          pagination={true}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          order={pagination.order}
          orderBy={pagination.orderBy}
          onRequestSort={pagination.handleRequestSort}
          count={exits.data?.total}
          maintainExpandedState={true}
          expandedRowIds={rowIdsToForceExpanded}
          getRowId={(row) => row._id?.toString() ?? ''}
          collapsedContent={(row) => {
            return (
              <div
                className={`${classes.exitLogContainer} ${classes.cmTableDarkHeading}`}
              >
                <Table
                  columns={getCollapsedTableColumns(row.exitLog || [])}
                  tablebody={row.exitLog || []}
                />
              </div>
            );
          }}
          TableFooter={
            <tfoot>
              <FooterRow>
                <FooterCell colSpan={isSuperAdmin ? 3 : 2} />
                <FooterCell>
                  {numberToCurrencyString(
                    sumBy(exits.data?.result, 'valueAfterDeductions'),
                  )}
                </FooterCell>
                <FooterCell>
                  {numberToCurrencyString(
                    sumBy(exits.data?.result, 'taxableValue'),
                  )}
                </FooterCell>
                <FooterCell>
                  {numberToCurrencyString(
                    sumBy(exits.data?.result, 'accruedFeesCharged'),
                  )}
                </FooterCell>
                <FooterCell>
                  {numberToCurrencyString(
                    sumBy(exits.data?.result, 'performanceFeesCharged'),
                  )}
                </FooterCell>
                <FooterCell>
                  {numberToDisplayString(
                    sumBy(exits.data?.result, 'noSharesExited'),
                  )}
                </FooterCell>
                <FooterCell />
                <FooterCell />
              </FooterRow>
            </tfoot>
          }
        />
      </PageContent>
      <AlertDialog
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        title="Are you sure you wish to delete this exit?"
        content="This cannot be undone and all information relating to the exit, including any published exit statements, will be lost."
        btnLabels={{ confirm: 'Delete exit', cancel: 'Go back' }}
        cancelBtnProps={{
          variant: 'outlined',
          color: 'primary',
        }}
        confirmBtnProps={{
          color: 'error',
          disabled: deleteExit.isPending || deleteAmendment.isPending,
          loading: deleteExit.isPending || deleteAmendment.isPending,
        }}
        onConfirm={onDeleteExit}
      />
    </PageContainer>
  );
};

export default ListExits;
